/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook usePostEligibilityRequest
 */
import _ from 'lodash';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import api from 'client/index';
import AppEvents from '@veritable/common/utils/appEvents';

import { getClaimEligibilityPath, getEligibilityResponsePath } from 'utils/router';
import { invalidateMetadata } from 'app-constants/queryKeys';
import { useAppStoreValues } from 'store/app';

type IParams = {
	model: EligibilityRequestForm.ISubmitModel;
	coverageId?: string;
};

/**
 * Mutate for post eligibility request
 */
const usePostEligibilityRequest = (): UseMutationResult<EligibilityRequests.IEligibilityData[], IAxiosError, IParams, unknown> => {
	const { isARCustomer } = useAppStoreValues().userAccess;
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ model, coverageId }) => {
			if (isARCustomer) {
				const { data: response } = await api.EzapiApi.postPatientBasedEligibilityRequests({
					eligibilityRequest: [model],
					coverageId: coverageId ?? '',
				});
				return response as EligibilityRequests.IEligibilityData[];
			}
			const { data } = await api.EzapiApi.postEligibilityRequests({
				eligibilityRequest: [model],
			});
			return data as EligibilityRequests.IEligibilityData[];
		},
		onSuccess: (data) => {
			AppEvents.publishApiSuccess();
			invalidateMetadata(queryClient);
			// redirect to output screen
			const redirectPath = isARCustomer
				? getClaimEligibilityPath(_.first(data)?.responseId)
				: getEligibilityResponsePath(_.first(data)?.responseId);
			AppEvents.publishRedirect({ url: redirectPath });
		},
		onError: (error) => {
			AppEvents.publishApiError({ error });
		},
	});
};

export default usePostEligibilityRequest;
